import React from 'react';
import { useFeatures } from '../../../context/FeatureContext';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';

/**
 * AiCreditsIndicator
 * Displays the current AI credits balance with a color-coded status indicator.
 * Shows tooltip with detailed credit information on hover.
 */
const AiCreditsIndicator = ({ className = '' }) => {
    const { entitlements } = useFeatures();

    if (!entitlements?.aiCredits) {
        return null;
    }

    const { balance, monthlyAllocation, fromPlan, purchased } = entitlements.aiCredits;
    const percentage = monthlyAllocation > 0 ? (balance / monthlyAllocation) * 100 : 0;

    // Determine badge color based on remaining percentage
    let variant = 'success';
    if (percentage <= 10) {
        variant = 'danger';
    } else if (percentage <= 25) {
        variant = 'warning';
    } else if (percentage <= 50) {
        variant = 'info';
    }

    const tooltipContent = (
        <Tooltip id="ai-credits-tooltip">
            <div style={{ textAlign: 'left' }}>
                <strong>AI-krediter</strong>
                <div>Kvar: {balance} av {monthlyAllocation}</div>
                {purchased > 0 && <div>Varav k&ouml;pta: {purchased}</div>}
                <div>Fr&aring;n plan: {fromPlan}</div>
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement="bottom"
            overlay={tooltipContent}
        >
            <Badge
                bg={variant}
                className={`ai-credits-badge ${className}`}
                style={{ cursor: 'pointer' }}
            >
                <i className="fas fa-robot me-1"></i>
                {balance}
            </Badge>
        </OverlayTrigger>
    );
};

/**
 * AiCreditsBar
 * Displays a progress bar showing AI credits usage.
 */
export const AiCreditsBar = ({ className = '', showLabel = true }) => {
    const { entitlements } = useFeatures();

    if (!entitlements?.aiCredits) {
        return null;
    }

    const { balance, monthlyAllocation } = entitlements.aiCredits;
    const percentage = monthlyAllocation > 0
        ? Math.min(100, Math.round((balance / monthlyAllocation) * 100))
        : 0;

    // Determine bar color
    let barClass = 'bg-success';
    if (percentage <= 10) {
        barClass = 'bg-danger';
    } else if (percentage <= 25) {
        barClass = 'bg-warning';
    } else if (percentage <= 50) {
        barClass = 'bg-info';
    }

    return (
        <div className={`ai-credits-bar ${className}`}>
            {showLabel && (
                <div className="d-flex justify-content-between mb-1">
                    <small>AI-krediter</small>
                    <small>{balance} / {monthlyAllocation}</small>
                </div>
            )}
            <div className="progress" style={{ height: '6px' }}>
                <div
                    className={`progress-bar ${barClass}`}
                    role="progressbar"
                    style={{ width: `${percentage}%` }}
                    aria-valuenow={percentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                />
            </div>
        </div>
    );
};

/**
 * useAiCredits
 * Hook to access AI credits data directly
 */
export const useAiCredits = () => {
    const { entitlements } = useFeatures();

    if (!entitlements?.aiCredits) {
        return {
            hasCredits: false,
            balance: 0,
            monthlyAllocation: 0,
            percentage: 0,
            isLow: false,
            isCritical: false
        };
    }

    const { balance, monthlyAllocation } = entitlements.aiCredits;
    const percentage = monthlyAllocation > 0 ? (balance / monthlyAllocation) * 100 : 0;

    return {
        hasCredits: balance > 0,
        balance,
        monthlyAllocation,
        percentage,
        isLow: percentage <= 25,
        isCritical: percentage <= 10,
        ...entitlements.aiCredits
    };
};

export default AiCreditsIndicator;
